import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from './Layout';
import '@chatscope/chat-ui-kit-styles/dist/default/styles.min.css';
import hasFeatureAccess from './utils/featureAccess';
import ChatBox from './components/ChatBox';
import { Box, FormControl, InputLabel, Select, MenuItem, Snackbar, Alert } from '@mui/material';

function ChatPage() {
  const [searchQuery, setSearchQuery] = useState(''); 
  const [messages, setMessages] = useState([]);
  const [threadId, setThreadId] = useState(null);
  const [runId, setRunId] = useState(null);
  const [doPoll, setDoPoll] = useState(false);
  const [pollNum, setPollNum] = useState(0);
  const [billSessions, setBillSessions] = useState([]);
  const [selectedSession, setSelectedSession] = useState('Current');
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const navigate = useNavigate();
  const userId = localStorage.getItem('user_id');
  const messageIds = [];
  const orgPackage = localStorage.getItem('package');
  const [remainingChats, setRemainingChats] = useState(0);

  useEffect(() => {
    const fetchBillSessions = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/bills/state-sessions/${userId}`, {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Token ${process.env.REACT_APP_API_TOKEN}`,
          },
        });

        if (!response.ok) {
          throw new Error('Failed to fetch bill sessions');
        }

        const data = await response.json();
        setBillSessions(data.sessions || []);
        if (data.sessions && data.sessions.length > 0) {
          setSelectedSession(data.sessions[0].id); // Most recent session
        }
      } catch (error) {
        console.error('Error fetching bill sessions:', error);
      }
    };

    fetchBillSessions();
  }, [userId]);

  // Find the most recent session (first in the array)
  const mostRecentSession = billSessions.length > 0 ? billSessions[0].id : '';

  const handleSessionChange = (e) => {
    const value = e.target.value;
    if (value !== mostRecentSession) {
      setSnackbarOpen(true);
      // Don't change the session
      return;
    }
    setSelectedSession(value);
  };

  useEffect(() => {
    if (!hasFeatureAccess('chatbot')) {
      navigate('/access-denied');
    }
  }, [navigate]);

  useEffect(() => {
    if (orgPackage === 'Free') {
      fetch(`${process.env.REACT_APP_API_URL}/chat-remaining/${userId}`, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Token ${process.env.REACT_APP_API_TOKEN}`,
        },
      })
        .then(response => response.json())
        .then(data => {
          setRemainingChats(data.remaining_chats);
        })
        .catch(error => console.error('Error fetching remaining chats:', error));
    } else {
      setRemainingChats(100);
    }
  }, [orgPackage, userId]);

  const handleSendMessage = (message) => {
    setRemainingChats(prevRemainingChats => prevRemainingChats - 1);

    setMessages([...messages, { content: message, direction: 'outgoing' }]);

    const thinkingMessage = { content: "We're thinking...", direction: 'incoming' };
    setMessages(prevMessages => [...prevMessages, thinkingMessage]);    
    
    fetch(`${process.env.REACT_APP_API_URL}/chat-all/${userId}?q=${encodeURIComponent(message)}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Token ${process.env.REACT_APP_API_TOKEN}`,
      }
    })
      .then(response => response.json())
      .then(data => {
        setRunId(data.run_id);
        setThreadId(data.thread_id);
        setDoPoll(true);
      })
      .catch(error => console.error('Error fetching reply:', error));
  };

  

  const formatResponse = (text_response, json_data) => {
    let content = text_response;

    if (json_data != null) {
      let bills = '';
      if (Array.isArray(json_data)) {
        for (let i = 0; i < json_data.length; i++) {
          if (json_data[i].link && json_data[i].link.length > 0){
            let queryStr = '';
            if (json_data[i].original_text) {
              queryStr = "&originalText=" + encodeURIComponent(json_data[i].original_text);
            }
            bills += '<a target="_blank" href="' + json_data[i].link + queryStr + '">' + json_data[i].bill_number + ": " + json_data[i].title + "</a>\n";
          }
        }
      } else {
        let queryStr = '';

        if (json_data.link && json_data.link.length > 0){
          if (json_data.original_text) {
            queryStr = "&originalText=" + encodeURIComponent(json_data.original_text);
          }
          bills += '<a target="_blank" href="' + json_data.link + queryStr + '">' + json_data.bill_number + ": " + json_data.title + "</a>\n";
        }
      }

      if (bills != '') {
        content += "\n\n<b>This answer contains references to the following bills:</b>\n" + bills;
      }
    }

    return content;
  };

  useEffect(() => {
    fetch(`${process.env.REACT_APP_API_URL}/user-chat/${userId}/0`, {
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Token ${process.env.REACT_APP_API_TOKEN}`,
      },
    })
      .then(response => response.json())
      .then(data => {
        data.forEach(item => {
          if (messageIds.includes(item.id)) {
            return;
          }

          const answerData = JSON.parse(item.answer_text);
          const content = formatResponse(answerData.text_response, answerData.json_data);

          setMessages(prevMessages => [...prevMessages, { content: item.question_text, direction: 'outgoing' }]);
          setMessages(prevMessages => [...prevMessages, { content: content, direction: 'incoming', original: item.original_text }]);
          messageIds.push(item.id);
        });
      })
      .catch(error => console.error('Error fetching user chat data:', error));
  }, [userId]); // Ensure this runs only once on mount and when userId changes

  useEffect(() => {
    let interval;
    if (doPoll) {
      interval = setInterval(() => {
        fetch(`${process.env.REACT_APP_API_URL}/chat-poll-no-json/${threadId}/${runId}/${userId}`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Token ${process.env.REACT_APP_API_TOKEN}`,
          }
        })
          .then(response => response.json())
          .then(data => {
            if (data.text_response != null){
              const content = formatResponse(data.text_response, data.json_data);

              setMessages(prevMessages => {
                const newMessages = [...prevMessages];
                newMessages[newMessages.length - 1] = { content: content, direction: 'incoming', original: data.original_text };
                return newMessages;
              });
              setDoPoll(false);
              setPollNum(0);
            }
            else {
              setPollNum(prevPollNum => {
                let newMessage = '';
                const newPollNum = prevPollNum + 1;
                if (newPollNum > 5) {
                  newMessage = "We're still working...";

                  if (newPollNum > 10) {
                    newMessage = "We're still working... This is taking longer than expected. Please be patient.";
                  }

                  setMessages(prevMessages => {
                    const newMessages = [...prevMessages];
                    newMessages[newMessages.length - 1] = { content: newMessage, direction: 'incoming' };
                    return newMessages;
                  });
                }
                return newPollNum;
              });
            }
          })
          .catch(error => console.error('Error polling:', error));
      }, 2000);
    }

    return () => clearInterval(interval);
  }, [doPoll, threadId, runId, userId]);

  return (
    <Layout searchQuery={searchQuery}>
      {/* Session Selector at the top */}
      <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2, mt: 2 }}>
        <FormControl
          sx={{
            minWidth: 100,
            maxWidth: '25%',
            flex: 1,
          }}
          size="medium"
        >
          <InputLabel id="session-select-label">Select a Year/Session</InputLabel>
          <Select
            labelId="session-select-label"
            value={selectedSession}
            label="Select a Year/Session"
            onChange={handleSessionChange}
            sx={{ width: '100%' }}
            MenuProps={{
              PaperProps: {
                style: {
                  maxHeight: 300,
                  width: 'auto',
                },
              },
            }}
          >
            {billSessions.map(session => (
              <MenuItem
                key={session.id}
                value={session.id}
                disabled={session.id !== mostRecentSession}
              >
                {session.name || session.label || session.id}
                {session.id !== mostRecentSession ? " (Restricted)" : ""}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
      </Snackbar>
      <ChatBox
        messages={messages}
        onSend={handleSendMessage}
        remainingChats={remainingChats}
        orgPackage={orgPackage}
      />
    </Layout>
  );
}

export default ChatPage;